// ./src/components/features/Policies/PoliciesFilterBar/PoliciesFilterBar.tsx
//
// Filter controls for the Policies list: by status (active / disabled). Fully
// controlled — the page owns the value and maps it to a PolicyFilter.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";

export type PolicyStatusFilter = "active" | "disabled";

export interface PoliciesFilterValue {
  status: PolicyStatusFilter | null;
}

export interface PoliciesFilterBarProps {
  value: PoliciesFilterValue;
  onChange: (value: PoliciesFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_POLICIES_FILTER: PoliciesFilterValue = {
  status: null,
};

const isFilterActive = (value: PoliciesFilterValue): boolean => Boolean(value.status);

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };

const ANY = "any";

const PoliciesFilterBarComponent: React.FC<PoliciesFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["policies", "common"]);

  const statusOptions = [
    { value: ANY, label: t("policies:filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("policies:filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("policies:filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("policies:filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as PolicyStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_POLICIES_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const PoliciesFilterBar = React.memo(PoliciesFilterBarComponent);
